import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mic } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const Auth = () => {
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(location.pathname === '/login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup, login } = useAuth();
  const navigate = useNavigate();
  
  // Get redirect and billing params from URL
  const searchParams = new URLSearchParams(location.search);
  const redirectPath = searchParams.get('redirect');
  const billingPeriod = searchParams.get('billing');

  // Update isLogin when pathname changes
  useEffect(() => {
    setIsLogin(location.pathname === '/login');
  }, [location.pathname]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Welcome back!');
        
        // Check if redirect is checkout (directly to Stripe)
        if (redirectPath === 'checkout') {
          const finalBillingPeriod = billingPeriod || localStorage.getItem('billingPeriod') || 'monthly';
          localStorage.removeItem('billingPeriod');
          try {
            const origin = window.location.origin;
            const response = await axios.post(`${API}/subscription/create-checkout`, {
              origin_url: origin,
              billing_period: finalBillingPeriod
            }, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });

            if (response.data.url) {
              // Redirect directly to Stripe checkout
              window.location.href = response.data.url;
              return;
            }
          } catch (error) {
            console.error('Failed to create checkout:', error);
            // Fallback to subscribe page if checkout creation fails
            navigate(`/subscribe?billing=${finalBillingPeriod}`);
            return;
          }
        }
        
        // Check URL params first, then localStorage for billing preference
        const finalBillingPeriod = billingPeriod || localStorage.getItem('billingPeriod');
        if (finalBillingPeriod) {
          localStorage.removeItem('billingPeriod');
          navigate(`/subscribe?billing=${finalBillingPeriod}`);
        } else if (redirectPath && redirectPath !== 'checkout') {
          navigate(redirectPath);
        } else {
          navigate('/app');
        }
      } else {
        await signup(email, password);
        toast.success('Account created! Welcome to Logly!');
        
        // Check if redirect is checkout (directly to Stripe)
        if (redirectPath === 'checkout') {
          const finalBillingPeriod = billingPeriod || localStorage.getItem('billingPeriod') || 'monthly';
          localStorage.removeItem('billingPeriod');
          try {
            const origin = window.location.origin;
            const response = await axios.post(`${API}/subscription/create-checkout`, {
              origin_url: origin,
              billing_period: finalBillingPeriod
            }, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });

            if (response.data.url) {
              // Redirect directly to Stripe checkout
              window.location.href = response.data.url;
              return;
            }
          } catch (error) {
            console.error('Failed to create checkout:', error);
            // Fallback to subscribe page if checkout creation fails
            navigate(`/subscribe?billing=${finalBillingPeriod}`);
            return;
          }
        }
        
        // Check URL params first, then localStorage for billing preference
        const finalBillingPeriod = billingPeriod || localStorage.getItem('billingPeriod');
        if (finalBillingPeriod) {
          localStorage.removeItem('billingPeriod');
          navigate(`/subscribe?billing=${finalBillingPeriod}`);
        } else if (redirectPath && redirectPath !== 'checkout') {
          navigate(redirectPath);
        } else {
          navigate('/app');
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error('Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 50%, #80deea 100%)' }}>
      <Card className="w-full max-w-md shadow-2xl border-0" data-testid="auth-card">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
              <Mic className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent" style={{ fontFamily: 'Inter, sans-serif' }}>Logly</CardTitle>
          <CardDescription className="text-base">
            {isLogin ? 'Welcome back! Log in to continue' : 'Start your 7-day free trial'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                data-testid="password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold py-6 text-lg"
              disabled={loading}
              data-testid="auth-submit-button"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Log In' : 'Sign Up - Start Free Trial')}
            </Button>
          </form>
          
          {/* Forgot Password Link - Only show in login mode */}
          {isLogin && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-cyan-600 hover:text-cyan-800 hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </button>
            </div>
          )}
          
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate(isLogin ? '/signup' : '/login')}
              className="text-cyan-600 hover:text-cyan-800 font-medium"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Log in'}
            </button>
          </div>
          {!isLogin && (
            <p className="mt-4 text-xs text-gray-500 text-center">
              By signing up, you'll get a 7-day free trial. After that, it's $2.99/month.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
